// Quick reminder picker — shared between Today (HabitItem) and Habits pages.
// Lets the user set / change / remove a habit's reminder_time without
// opening the full edit form. Saves to supabase; AuthContext's realtime
// subscription on `habits` will then reschedule local notifications.
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { rescheduleHabitReminders } from '../lib/localNotifications';
import { toast } from 'sonner';
import { BellOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';

export const HabitReminderDialog = ({ habit, open, onOpenChange }) => {
  const [val, setVal] = useState(habit?.reminder_time || '08:00');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setVal(habit?.reminder_time || '08:00');
  }, [open, habit?.reminder_time]);

  const save = async (time) => {
    if (!habit?.id) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from('habits')
        .update({ reminder_time: time })
        .eq('id', habit.id);
      if (error) throw error;
      // Reschedule immediately so the user doesn't have to open Settings.
      // (We don't rely solely on the realtime habits subscription because
      // it requires the table to be in supabase_realtime publication.)
      const r = await rescheduleHabitReminders();
      if (time) {
        if (r.skipped === 'no-permission') {
          toast.warning('Reminder saved — but enable notifications in Settings to receive it.');
        } else {
          toast.success(`Reminder set for ${time}${r.scheduled ? ` · ${r.scheduled} active` : ''}`);
        }
      } else {
        toast.success('Reminder removed');
      }
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.message || 'Failed to save reminder');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`habit-reminder-dialog-${habit?.id}`}>
        <DialogHeader>
          <DialogTitle>Reminder · {habit?.name}</DialogTitle>
          <DialogDescription>
            Pick a time and we&apos;ll quietly nudge you so you can keep the streak alive.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Time</label>
            <Input
              type="time"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="mt-2 max-w-[140px]"
              data-testid={`habit-reminder-time-${habit?.id}`}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {['07:00', '08:00', '12:00', '18:00', '21:00'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setVal(t)}
                className={`text-xs px-3 py-1.5 rounded-full border ${val === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
                data-testid={`habit-reminder-preset-${habit?.id}-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center gap-2 pt-2">
            {habit?.reminder_time ? (
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => save(null)}
                disabled={busy}
                data-testid={`habit-reminder-remove-${habit?.id}`}
              >
                <BellOff className="h-4 w-4 mr-1.5" /> Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy}>Cancel</Button>
              <Button onClick={() => save(val)} disabled={busy || !val} data-testid={`habit-reminder-save-${habit?.id}`}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
